/**
 * TAS-517 (interim) — what makes an hourly row NPM SERVICE-ORDER work.
 *
 * THIS IS A MIRROR OF `API/src/util/serviceOrder/serviceOrderRow.ts`. That module is the
 * authority: it is what the Vista exporter (`src/riceServices/timecards/timecardProcessor.ts`)
 * and the Post Verification report actually run. Anything here that drifts from it turns
 * this app's pre-run counts and warnings into lies, so change the two together or not at
 * all — the API file carries the full reasoning and the field-by-field notes.
 *
 * The short version: an NPM service order can be recorded three ways, and until TAS-507
 * only the first existed —
 *
 *   legacy     `ServiceOrderText`, free text typed by the employee;
 *   resolved   `ServiceOrderID` (+ scope), written when mobile matches the punch to a
 *              real Vista work order — WHICH CLEARS `ServiceOrderText`;
 *   temporary  `TempServiceOrderDescription`, a number Vista has not seen yet.
 *
 * The exporter's service-order branch used to key on non-blank `ServiceOrderText` alone,
 * so from the moment mobile started resolving orders a resolved punch matched none of the
 * three export branches and vanished. The API test was widened to all three shapes; this
 * is the same test, for the screens that predict what that export will do.
 *
 * NPM ONLY IN PRACTICE. `serviceOrderID`, `tempServiceOrderDescription` and the scope
 * columns are written by the NPM/B2C mobile path alone and are absent everywhere else, so
 * on any other tenant this collapses to the legacy free-text test it always was. Callers
 * that render service-order work still gate on `config.tehPunchesAreHourly` — on Wallcraft
 * `ServiceOrderText` is a PURCHASE ORDER number and must never be read as one of these.
 */

/** The subset of an hourly group / punch that decides service-order-ness. */
export interface ServiceOrderRowFields {
  /** Legacy free text typed by the employee. */
  serviceOrderText?: string | null;
  /** TAS-507 — FK to a validated `ServiceOrder`; set only once mobile resolves the punch. */
  serviceOrderID?: number | null;
  /** TAS-507 — the resolved order's Vista number (`ServiceOrder.AltServiceOrderID`). */
  serviceOrderAltID?: string | null;
  /** TAS-507 — a temporary service order the employee typed that is not in Vista yet. */
  tempServiceOrderDescription?: string | null;
}

/**
 * The LEGACY test, character for character with the API: `length > 0`, NOT trimmed.
 *
 * A whitespace-only free text IS a service-order row and posts a memo with those spaces
 * in it. Trimming here would quietly reclassify that punch as unexportable — the exact
 * failure this whole change exists to remove. Pinned deliberately, on both sides.
 */
const hasLegacyText = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.length > 0;

/** The new (TAS-507) fields ARE trimmed — nothing depends on their raw form. */
const nonBlank = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * True when this hourly row is service-order work in ANY of its three recorded forms.
 *
 * Note what is NOT in here: `serviceOrderAltID`. It rides along only as the display /
 * memo form of a row that already has `serviceOrderID`, and the API's test ignores it for
 * the same reason. See `describeNpmServiceOrder` for the one defensive exception.
 */
export const isServiceOrderRow = (row: ServiceOrderRowFields): boolean =>
  hasLegacyText(row.serviceOrderText)
  || row.serviceOrderID != null
  || nonBlank(row.tempServiceOrderDescription);

/**
 * The service order as text — byte for byte the Vista `Memo` the exporter will send.
 *
 * Order matters and matches the API: the VALIDATED Vista number wins, because it is the
 * one thing NPM can look up; free text comes next so a legacy punch keeps the memo it has
 * always had; the temporary description is the last resort, being all a temp punch has.
 * The free text is returned RAW (see `hasLegacyText`).
 *
 * Returns `''` for a row that is not service-order work at all — test `isServiceOrderRow`
 * first if that distinction matters.
 */
export const serviceOrderMemoText = (row: ServiceOrderRowFields): string => {
  if (nonBlank(row.serviceOrderAltID)) return row.serviceOrderAltID!.trim();
  if (hasLegacyText(row.serviceOrderText)) return row.serviceOrderText!;
  if (nonBlank(row.tempServiceOrderDescription)) return row.tempServiceOrderDescription!.trim();
  return '';
};
